import { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { BACKEND_URL } from "../lib/api";

const resolveImg = (url) => (url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url);

/**
 * Editorial image gallery with thumbnail strip + click-to-zoom lightbox.
 *
 * Props:
 *  - images: string[] — full ordered list (cover first, then gallery photos)
 *  - title:  string   — used for alt + lightbox caption
 *  - artist: string   — optional, appended to alt text for SEO keywords
 *  - medium: string   — optional, appended to alt text for SEO keywords
 */
export function ArtworkImageGallery({ images, title, artist, medium }) {
  const safeImages = (images || []).filter(Boolean);
  const [activeIdx, setActiveIdx] = useState(0);
  const [lightboxIdx, setLightboxIdx] = useState(-1);

  // Pre-compose the descriptive alt prefix once so every <img> stays consistent
  // and the SEO keywords (medium, artist) are present on every photo.
  const altBase = [
    title,
    medium ? medium.toLowerCase() : null,
    artist ? `by ${artist}` : null,
  ]
    .filter(Boolean)
    .join(" — ");

  const total = safeImages.length;
  const showThumbs = total > 1;
  const openLightbox = (idx) => setLightboxIdx(idx);
  const closeLightbox = useCallback(() => setLightboxIdx(-1), []);
  const next = useCallback(
    () => setLightboxIdx((i) => (i + 1) % total),
    [total]
  );
  const prev = useCallback(
    () => setLightboxIdx((i) => (i - 1 + total) % total),
    [total]
  );

  // Keyboard nav for the lightbox
  useEffect(() => {
    if (lightboxIdx < 0) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    // lock scroll while open
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = original;
    };
  }, [lightboxIdx, closeLightbox, next, prev]);

  if (total === 0) return null;
  const active = safeImages[activeIdx] || safeImages[0];

  return (
    <div data-testid="artwork-image-gallery">
      <button
        type="button"
        onClick={() => openLightbox(activeIdx)}
        data-testid="artwork-main-image-btn"
        aria-label="Open in lightbox"
        className="block w-full bg-[#F2EDE5] cursor-zoom-in"
      >
        <img
          src={resolveImg(active)}
          alt={altBase}
          className="w-full h-auto max-h-[80vh] object-contain"
          data-testid="artwork-image"
        />
      </button>

      {showThumbs && (
        <div className="mt-4 grid grid-cols-5 sm:grid-cols-6 lg:grid-cols-9 gap-2" data-testid="artwork-thumb-strip">
          {safeImages.map((url, i) => (
            <button
              key={`${url}-${i}`}
              type="button"
              onClick={() => setActiveIdx(i)}
              onDoubleClick={() => openLightbox(i)}
              data-testid={`artwork-thumb-${i}`}
              aria-label={`Show photo ${i + 1}`}
              aria-pressed={i === activeIdx}
              className={`aspect-square overflow-hidden border-2 transition-all ${
                i === activeIdx
                  ? "border-[#1E3A8A]"
                  : "border-transparent hover:border-[#E5E0D8] opacity-70 hover:opacity-100"
              }`}
            >
              <img
                src={resolveImg(url)}
                alt={`${altBase} — photo ${i + 1}`}
                className="w-full h-full object-cover bg-[#F4EFE6]"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      )}

      {lightboxIdx >= 0 && (
        <div
          data-testid="artwork-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={title}
          className="fixed inset-0 z-50 bg-[#111]/95 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              closeLightbox();
            }}
            aria-label="Close"
            data-testid="lightbox-close"
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
          >
            <X className="w-6 h-6" strokeWidth={1.5} />
          </button>
          {total > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  prev();
                }}
                aria-label="Previous"
                data-testid="lightbox-prev"
                className="absolute left-4 md:left-8 text-white/80 hover:text-white p-3"
              >
                <ChevronLeft className="w-8 h-8" strokeWidth={1.5} />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  next();
                }}
                aria-label="Next"
                data-testid="lightbox-next"
                className="absolute right-4 md:right-8 text-white/80 hover:text-white p-3"
              >
                <ChevronRight className="w-8 h-8" strokeWidth={1.5} />
              </button>
            </>
          )}
          <img
            src={resolveImg(safeImages[lightboxIdx])}
            alt={altBase}
            data-testid="lightbox-image"
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          {total > 1 && (
            <span className="absolute bottom-6 left-1/2 -translate-x-1/2 label-eyebrow text-white/70">
              {lightboxIdx + 1} / {total}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default ArtworkImageGallery;
